// import React from "react";
// import { Box, Grid, Typography, TextField, Button, Paper } from "@mui/material";
// import PublicLayout from "../../layouts/PublicLayout";
// import { Container } from "@mui/material";

// function Pricing(props) {
//   return (
//     <PublicLayout>
//       <Container maxWidth="lg">
//         <Box>Explore all features</Box>
//       </Container>
//     </PublicLayout>
//   );
// }

// export default Pricing;

import React from "react";
import {
  Box,
  Grid,
  Typography,
  Button,
  Paper,
  Chip,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Divider,
  Container,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import CheckIcon from "@mui/icons-material/Check";
import CloseIcon from "@mui/icons-material/Close";
import StarIcon from "@mui/icons-material/Star";
import PublicLayout from "../../layouts/PublicLayout";
import { Link as RouterLink } from "react-router-dom";

const plans = [
  {
    id: "basic",
    name: "Starter",
    priceMonthly: 1499,
    priceAnnual: 1499 * 10, // example annual discount
    description: "All core POS essentials for single outlet operations.",
    bullets: [
      "Unlimited billing",
      "Menu & categories",
      "Basic inventory",
      "1 outlet, 2 users",
    ],
    cta: "Start free demo",
    recommended: false,
  },
  {
    id: "pro",
    name: "Pro",
    priceMonthly: 3499,
    priceAnnual: 3499 * 10,
    description: "Advanced features for multi-outlet & growing businesses.",
    bullets: [
      "Everything in Starter",
      "Multi-outlet management",
      "Advanced inventory & reports",
      "Priority support",
    ],
    cta: "Book a Demo",
    recommended: true,
  },
];

const featureHighlights = [
  {
    title: "Offline-first reliability",
    desc: "Continue selling even when internet is flaky — automatic sync when online.",
    icon: "⚡",
  },
  {
    title: "Kitchen & KOT workflows",
    desc: "Fast kitchen routing, KOT tracking and clear fulfilment states.",
    icon: "🍽️",
  },
  {
    title: "Inventory + Low-stock alerts",
    desc: "Track consumption on each sale and get proactive reorder alerts.",
    icon: "📦",
  },
  {
    title: "Detailed reporting",
    desc: "Sales, staff, taxes & outlet-level insights — exportable CSV/PDF.",
    icon: "📊",
  },
];

const faqs = [
  {
    q: "Do you offer a free trial?",
    a: "Yes — we offer a free demo and trial period. Book a demo and our sales team will set you up.",
  },
  {
    q: "Can I change plan later?",
    a: "Absolutely. You can upgrade or downgrade anytime. Changes are applied from next billing cycle.",
  },
  {
    q: "Do you charge for additional outlets?",
    a: "The Pro plan covers multi-outlet needs. For enterprise-level deployments we offer custom pricing.",
  },
  {
    q: "Is data migration available?",
    a: "Yes — we help migrate menu and historical sales data for paid plans (migration service may have a one-time fee).",
  },
];

export default function Pricing() {
  return (
    <PublicLayout>
      <Container maxWidth="lg">
        <Box sx={{ bgcolor: "background.paper", pb: 10 }}>
          {/* HERO */}
          <Container sx={{ maxWidth: "1200px", pt: { xs: 6, md: 10 } }}>
            <Grid container spacing={4} alignItems="center">
              <Grid item size={{ xs: 12, md: 7 }}>
                <Typography variant="h3" sx={{ fontWeight: 800, mb: 2 }}>
                  Pricing built for restaurants & cafés
                </Typography>
                <Typography variant="h6" color="text.secondary" sx={{ mb: 3 }}>
                  Choose simplicity or scale — both plans include core POS
                  features, secure cloud sync & local offline mode. Start with a
                  demo and pick the plan that fits your growth.
                </Typography>

                <List disablePadding>
                  <ListItem disableGutters>
                    <ListItemIcon sx={{ minWidth: 36 }}>
                      <CheckIcon color="success" />
                    </ListItemIcon>
                    <ListItemText primary="No hidden fees — transparent billing" />
                  </ListItem>
                  <ListItem disableGutters>
                    <ListItemIcon sx={{ minWidth: 36 }}>
                      <CheckIcon color="success" />
                    </ListItemIcon>
                    <ListItemText primary="Quick onboarding — demo & setup assistance" />
                  </ListItem>
                </List>
              </Grid>

              <Grid item size={{ xs: 12, md: 5 }}>
                <Paper
                  elevation={2}
                  sx={{
                    borderRadius: 2,
                    p: 3,
                    bgcolor: "common.white",
                  }}
                >
                  <Typography variant="subtitle2" color="text.secondary">
                    Need help choosing?
                  </Typography>
                  <Typography variant="h6" sx={{ fontWeight: 700, mb: 1 }}>
                    Talk to our sales team
                  </Typography>
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{ mb: 2 }}
                  >
                    We’ll recommend the best plan for your number of outlets,
                    staff and expected transaction volume.
                  </Typography>
                  <Button
                    variant="contained"
                    fullWidth
                    sx={{ backgroundColor: "#f6521a", color: "#fff", py: 1.2 }}
                  >
                    Contact Sales
                  </Button>
                </Paper>
              </Grid>
            </Grid>
          </Container>

          {/* PRICING CARDS */}
          <Container sx={{ maxWidth: "1100px", mt: 6 }}>
            <Grid container spacing={3} justifyContent="center">
              {plans.map((p) => (
                <Grid item size={{ xs: 12, sm: 8, md: 6 }} key={p.id}>
                  <Paper
                    elevation={p.recommended ? 6 : 2}
                    sx={{
                      borderRadius: 3,
                      p: 4,
                      position: "relative",
                      overflow: "visible",
                      border: p.recommended
                        ? "1px solid rgba(246,82,26,0.12)"
                        : "1px solid #eee",
                    }}
                  >
                    {p.recommended && (
                      <Chip
                        label="Most popular"
                        color="warning"
                        icon={<StarIcon />}
                        sx={{
                          position: "absolute",
                          top: -16,
                          right: 16,
                          fontWeight: 700,
                        }}
                      />
                    )}

                    <Box
                      display="flex"
                      justifyContent="space-between"
                      alignItems="flex-start"
                      mb={2}
                    >
                      <Box>
                        <Typography variant="h6" sx={{ fontWeight: 700 }}>
                          {p.name}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          {p.description}
                        </Typography>
                      </Box>
                    </Box>

                    <Divider sx={{ my: 2 }} />

                    <List dense>
                      {p.bullets.map((b, i) => (
                        <ListItem key={i} disableGutters>
                          <ListItemIcon sx={{ minWidth: 36 }}>
                            <CheckIcon color="success" />
                          </ListItemIcon>
                          <ListItemText primary={b} />
                        </ListItem>
                      ))}
                    </List>

                    <Box mt={3} display="flex" gap={2}>
                      <Button
                        to="/pos/book-demo"
                        component={RouterLink}
                        variant={p.recommended ? "contained" : "outlined"}
                        fullWidth
                        sx={
                          p.recommended
                            ? {
                                backgroundColor: "#f6521a",
                                color: "#fff",
                                py: 1.2,
                              }
                            : { py: 1.2 }
                        }
                      >
                        {p.cta}
                      </Button>
                    </Box>
                  </Paper>
                </Grid>
              ))}
            </Grid>
          </Container>

          {/* FEATURE HIGHLIGHTS */}
          <Container sx={{ maxWidth: "1100px", mt: 6 }}>
            <Grid container spacing={3}>
              {featureHighlights.map((f, idx) => (
                <Grid key={idx} item size={{ xs: 12, sm: 6, md: 3 }}>
                  <Paper
                    elevation={0}
                    sx={{
                      p: 3,
                      borderRadius: 2,
                      bgcolor: "transparent",
                    }}
                  >
                    <Box
                      sx={{
                        width: 56,
                        height: 56,
                        borderRadius: 2,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        mb: 1,
                        background: "#fff",
                        boxShadow: 1,
                        fontSize: 22,
                      }}
                    >
                      {f.icon}
                    </Box>
                    <Typography variant="h6" sx={{ fontWeight: 700 }}>
                      {f.title}
                    </Typography>
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      sx={{ mt: 1 }}
                    >
                      {f.desc}
                    </Typography>
                  </Paper>
                </Grid>
              ))}
            </Grid>
          </Container>

          {/* FAQ */}
          <Container sx={{ maxWidth: "900px", mt: 6 }}>
            <Typography variant="h5" sx={{ fontWeight: 700, mb: 2 }}>
              Frequently asked questions
            </Typography>
            {faqs.map((f, i) => (
              <Accordion key={i} defaultExpanded={i === 0}>
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <Typography sx={{ fontWeight: 700 }}>{f.q}</Typography>
                </AccordionSummary>
                <AccordionDetails>
                  <Typography color="text.secondary">{f.a}</Typography>
                </AccordionDetails>
              </Accordion>
            ))}
          </Container>

          {/* FINAL CTA */}
          <Box sx={{ mt: 6, bgcolor: "#f6f7fb", py: 6 }}>
            <Container
              sx={{
                maxWidth: "1100px",
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                gap: 2,
              }}
            >
              <Box>
                <Typography variant="h5" sx={{ fontWeight: 800 }}>
                  Ready to transform your outlets?
                </Typography>
                <Typography color="text.secondary" sx={{ mt: 1 }}>
                  Book a demo and see how T-POS adapts to your workflows.
                </Typography>
              </Box>

              <Box display="flex" gap={2}>
                <Button variant="outlined">Contact Sales</Button>
                <Button
                  to="/pos/book-demo"
                  component={RouterLink}
                  variant="contained"
                  sx={{ backgroundColor: "#f6521a", color: "#fff" }}
                >
                  Book a Demo
                </Button>
              </Box>
            </Container>
          </Box>
        </Box>
      </Container>
    </PublicLayout>
  );
}
